import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { archiveQuestionSet, listQuestionSets } from "@/api/question-sets";
import { assignSetFolder, createFolder, listFolders } from "@/api/folders";
import { progressStore } from "@/practice/progress-store";
import { useProgressHydrated } from "@/practice/use-progress";
import { FrameworkBadge, formatRelativeDate, setMetaLine } from "@/train/set-card";
import { GlobalStatsBanner } from "@/stats/global-stats-banner";
import { SetStatusBadge, type SetStatus } from "./set-detail";
import { loadLibrarySort, saveLibrarySort, type LibrarySort } from "./sort-store";
import { Alert, Button, Spinner, cx } from "@/components/ui";

export function LibraryList() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const hydrated = useProgressHydrated();
  const [archived, setArchived] = useState(false);
  const [folder, setFolder] = useState<string | null>(null); // null = All
  const [newFolder, setNewFolder] = useState("");
  const [creating, setCreating] = useState(false);
  const [sort, setSortState] = useState<LibrarySort>(loadLibrarySort);
  const setSort = (s: LibrarySort) => {
    setSortState(s);
    saveLibrarySort(s);
  };

  const sets = useQuery({
    queryKey: ["question-sets", archived],
    queryFn: () => listQuestionSets(archived),
  });
  const folders = useQuery({ queryKey: ["folders"], queryFn: listFolders });

  const invalidateSets = () =>
    qc.invalidateQueries({ queryKey: ["question-sets"] });

  const createMut = useMutation({
    mutationFn: () => createFolder(newFolder.trim()),
    onSuccess: () => {
      setNewFolder("");
      setCreating(false);
      qc.invalidateQueries({ queryKey: ["folders"] });
    },
  });
  const assignMut = useMutation({
    mutationFn: (v: { setId: string; folderId: string | null }) =>
      assignSetFolder(v.setId, v.folderId),
    onSuccess: invalidateSets,
  });
  const archiveMut = useMutation({
    mutationFn: (v: { setId: string; archived: boolean }) =>
      archiveQuestionSet(v.setId, v.archived),
    onSuccess: invalidateSets,
  });

  const visible = (sets.data ?? [])
    .filter((s) => folder == null || s.folder_id === folder)
    .sort((a, b) =>
      sort === "name"
        ? a.title.localeCompare(b.title)
        : Date.parse(b.created_at) - Date.parse(a.created_at),
    );

  return (
    <div className="animate-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("Library")}</h1>
          <p className="mt-1 text-ink-faint">{t("Your question sets. Open one to practise.")}</p>
        </div>
        <Link to="/app/create">
          <Button>{t("Create a set")}</Button>
        </Link>
      </div>

      <div className="mt-5">
        <GlobalStatsBanner />
      </div>

      {/* Folder pills + archived toggle */}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Pill active={folder == null} onClick={() => setFolder(null)}>
          {t("All")}
        </Pill>
        {folders.data?.map((f) => (
          <Pill key={f.id} active={folder === f.id} onClick={() => setFolder(f.id)}>
            {f.name}
          </Pill>
        ))}
        {creating ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (newFolder.trim()) createMut.mutate();
            }}
            className="flex items-center gap-1"
          >
            <input
              autoFocus
              value={newFolder}
              onChange={(e) => setNewFolder(e.target.value)}
              placeholder={t("Folder name")}
              className="h-8 rounded-full border border-black/10 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
            <Button size="sm" type="submit" loading={createMut.isPending}>
              {t("Add")}
            </Button>
          </form>
        ) : (
          <Pill active={false} onClick={() => setCreating(true)}>
            {t("+ New")}
          </Pill>
        )}
        <div className="ml-auto inline-flex rounded-xl bg-black/5 p-1">
          {(["date", "name"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setSort(s)}
              className={cx(
                "rounded-lg px-3 py-1.5 text-sm font-semibold transition",
                sort === s ? "bg-white text-brand-700 shadow-soft" : "text-ink-faint",
              )}
            >
              {s === "date" ? t("Date") : t("Name")}
            </button>
          ))}
        </div>
        <button
          onClick={() => setArchived((v) => !v)}
          className="text-sm text-brand-600 hover:underline"
        >
          {archived ? t("← Active sets") : t("View archived")}
        </button>
      </div>

      {sets.isLoading && (
        <div className="flex justify-center py-20 text-brand-600">
          <Spinner className="h-7 w-7" />
        </div>
      )}
      {sets.isError && (
        <div className="mt-8">
          <Alert>{sets.error instanceof Error ? sets.error.message : t("Couldn't load your sets.")}</Alert>
        </div>
      )}

      {sets.data && visible.length === 0 && (
        <div className="mt-12 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-100 text-2xl">
            📚
          </div>
          <p className="text-ink-faint">
            {archived
              ? t("No archived sets.")
              : folder != null
                ? t("No sets in this folder.")
                : t("No sets yet. Generate your first one in Create.")}
          </p>
        </div>
      )}

      {visible.length > 0 && (
        <div className="mt-6 space-y-3">
          {visible.map((s) => {
            // Progress is read from the shared /learner/progress blob (same source as iOS
            // + the Train tab). `hydrated` gates it so rows don't flash empty→filled.
            const started = hydrated && progressStore.hasStarted(s.id);
            const stats = progressStore.lifetimeStats(s.id);
            const doneBulks = progressStore.completedBulkCount(s.id);
            const wrong = hydrated ? progressStore.wrongIds(s.id).size : 0;
            const lastAt = progressStore.lastActivityAt(s.id);
            const pct =
              s.bulk_count > 0 ? Math.min(100, Math.round((doneBulks / s.bulk_count) * 100)) : 0;
            const allDone = s.bulk_count > 0 && doneBulks >= s.bulk_count;
            const status: SetStatus = allDone
              ? "done"
              : started
                ? "in_progress"
                : "not_started";
            return (
              <div key={s.id} className="card flex items-center gap-3 p-4">
                <Link to={`/app/library/${s.id}`} className="flex min-w-0 flex-1 items-center gap-4">
                  <FrameworkBadge slug={s.source_framework_slug} className="h-11 w-11" />
                  <div className="min-w-0 flex-1">
                    <h2 className="truncate font-semibold text-ink">{s.title}</h2>
                    <p className="text-sm text-ink-faint">{setMetaLine(s, t)}</p>
                    {s.project_id && (
                      <span className="mt-1 inline-flex w-fit items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700">
                        📂 {s.project_title
                          ? t("From project: {{title}}", { title: s.project_title })
                          : t("From a project")}
                      </span>
                    )}
                    {started && s.bulk_count > 0 && (
                      <div className="mt-2 max-w-xs">
                        <div className="mb-1 flex items-center justify-between text-[11px] text-ink-faint">
                          <span>
                            {t("{{done}}/{{total}} sections", { done: doneBulks, total: s.bulk_count })}
                          </span>
                          {stats.accuracyPercent !== null && (
                            <span>{t("{{pct}}% correct", { pct: stats.accuracyPercent })}</span>
                          )}
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-black/5">
                          <div
                            className="h-full rounded-full bg-brand-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        {(wrong > 0 || lastAt) && (
                          <div className="mt-1 flex items-center justify-between text-[10px] text-ink-faint">
                            <span className={wrong > 0 ? "font-medium text-red-600" : ""}>
                              {wrong > 0 ? t("{{count}} to review", { count: wrong }) : ""}
                            </span>
                            {lastAt && <span>{t("Practised {{when}}", { when: formatRelativeDate(lastAt, t) })}</span>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Link>
                <div className="flex shrink-0 items-center gap-2">
                  {hydrated && <SetStatusBadge status={status} />}
                  {!archived && folders.data && folders.data.length > 0 && (
                    <select
                      value={s.folder_id ?? ""}
                      onChange={(e) =>
                        assignMut.mutate({ setId: s.id, folderId: e.target.value || null })
                      }
                      className="hidden rounded-lg border border-black/10 px-2 py-1.5 text-xs text-ink-soft focus:outline-none focus:ring-2 focus:ring-brand-400 sm:block"
                      title={t("Move to folder")}
                    >
                      <option value="">{t("No folder")}</option>
                      {folders.data.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                  )}
                  <button
                    onClick={() => archiveMut.mutate({ setId: s.id, archived: !archived })}
                    className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-ink-faint hover:bg-black/5"
                  >
                    {archived ? t("Restore") : t("Archive")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cx(
        "h-8 rounded-full px-3.5 text-sm font-medium transition",
        active ? "bg-brand-600 text-white" : "bg-black/5 text-ink-soft hover:bg-black/10",
      )}
    >
      {children}
    </button>
  );
}
